import { createSlice } from '@reduxjs/toolkit'

import { fetchSubstitutedArticleTitles } from '../requests/thunks'
import * as DynamakerContract from '../../integration/DynamakerContract'

interface ArticleSubstitutionsState {
  // articles DM has substituted, accumulated until the customer acknowledges them
  substitutedArticles: DynamakerContract.SubstitutedArticle[]
  // titles for the article numbers in substitutedArticles, keyed by erpItemNumber
  substitutedArticleTitles: Record<string, string>
  // reported articles DM had no substitute for, kept past the acknowledgement so the cart modal can still name them
  unresolvedErpItemNumbers: string[]
  // outbox emptied by App when the articles have been posted to DM
  missingErpItemNumbersToReport: string[]
  isAwaitingArticleSubstitutions: boolean
  // the three lists below are internal, and outlive substitutedArticles
  _substitutedErpItemNumbers: string[]
  // never emptied: this is what keeps us from reporting the same article twice
  _reportedMissingErpItemNumbers: string[]
  // everything Scensum didn't recognise, reported or not -- keeps us from asking about it again
  _knownMissingErpItemNumbers: string[]
}

const initialState: ArticleSubstitutionsState = {
  substitutedArticles: [],
  substitutedArticleTitles: {},
  unresolvedErpItemNumbers: [],
  missingErpItemNumbersToReport: [],
  isAwaitingArticleSubstitutions: false,
  _substitutedErpItemNumbers: [],
  _reportedMissingErpItemNumbers: [],
  _knownMissingErpItemNumbers: [],
}

const articleSubstitutionsSlice = createSlice({
  name: 'articleSubstitutions',
  initialState,
  reducers: {
    reportMissingArticles(state, action) {
      const { knownMissing, toReport }: { knownMissing: string[]; toReport: string[] } = action.payload

      state._knownMissingErpItemNumbers = state._knownMissingErpItemNumbers.concat(
        knownMissing.filter((erpItemNumber) => !state._knownMissingErpItemNumbers.includes(erpItemNumber))
      )

      const notYetReported = toReport.filter(
        (erpItemNumber) => !state._reportedMissingErpItemNumbers.includes(erpItemNumber)
      )

      if (notYetReported.length === 0) return

      state._reportedMissingErpItemNumbers = state._reportedMissingErpItemNumbers.concat(notYetReported)
      state.missingErpItemNumbersToReport = state.missingErpItemNumbersToReport.concat(notYetReported)
      state.isAwaitingArticleSubstitutions = true
    },
    clearMissingErpItemNumbersToReport(state) {
      state.missingErpItemNumbersToReport = []
    },
    setIsAwaitingArticleSubstitutions(state, action) {
      state.isAwaitingArticleSubstitutions = action.payload
    },
    addSubstitutedArticles(state, action) {
      const substitutedArticles: DynamakerContract.SubstitutedArticle[] = action.payload

      const withKnownOldArticle = substitutedArticles.filter((substitutedArticle) => {
        if (substitutedArticle.oldErpItemNumber === null) {
          console.error(
            `addSubstitutedArticles reducer: substituted article from DM has no oldErpItemNumber: ${JSON.stringify(
              substitutedArticle
            )}`
          )
          return false
        }
        return true
      })

      // DM reports only what the latest change substituted, so the rounds are accumulated here
      withKnownOldArticle.forEach((substitutedArticle) => {
        // X->Y followed by Y->Z is one substitution seen from the customer, so the chain is collapsed into X->Z
        const linkInChain = state.substitutedArticles.find(
          (existing) =>
            existing.newErpItemNumber !== null && existing.newErpItemNumber === substitutedArticle.oldErpItemNumber
        )

        if (linkInChain) {
          linkInChain.newErpItemNumber = substitutedArticle.newErpItemNumber
        } else {
          state.substitutedArticles.push(substitutedArticle)
        }
      })

      state._substitutedErpItemNumbers = state._substitutedErpItemNumbers.concat(
        withKnownOldArticle.flatMap((substitutedArticle) =>
          substitutedArticle.oldErpItemNumber &&
          !state._substitutedErpItemNumbers.includes(substitutedArticle.oldErpItemNumber)
            ? [substitutedArticle.oldErpItemNumber]
            : []
        )
      )
    },
    markReportedArticlesUnresolved(state) {
      const unresolved = state._reportedMissingErpItemNumbers.filter(
        (erpItemNumber) =>
          !state._substitutedErpItemNumbers.includes(erpItemNumber) &&
          !state.unresolvedErpItemNumbers.includes(erpItemNumber)
      )

      if (unresolved.length === 0) return

      state.unresolvedErpItemNumbers = state.unresolvedErpItemNumbers.concat(unresolved)
      state.substitutedArticles = state.substitutedArticles.concat(
        unresolved.map((oldErpItemNumber) => ({
          oldErpItemNumber,
          newErpItemNumber: null,
          reason: 'missing' as const,
        }))
      )
    },
    clearSubstitutedArticles(state) {
      state.substitutedArticles = []
      // The unresolved ones are still named in the add-to-cart modal, so their titles have to outlive the acknowledgement
      const remainingTitles: Record<string, string> = {}
      state.unresolvedErpItemNumbers.forEach((erpItemNumber) => {
        const title = state.substitutedArticleTitles[erpItemNumber]
        if (title) remainingTitles[erpItemNumber] = title
      })
      state.substitutedArticleTitles = remainingTitles
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchSubstitutedArticleTitles.fulfilled, (state, action) => {
      state.substitutedArticleTitles = {
        ...state.substitutedArticleTitles,
        ...action.payload,
      }
    })
  },
})

export const {
  reportMissingArticles,
  clearMissingErpItemNumbersToReport,
  setIsAwaitingArticleSubstitutions,
  addSubstitutedArticles,
  markReportedArticlesUnresolved,
  clearSubstitutedArticles,
} = articleSubstitutionsSlice.actions

export default articleSubstitutionsSlice.reducer
